import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {Props as PricePredictionProps} from './PricePrediction'

const Wrapper = styled.div`
  display: flex;
  &.--horizontal {
    justify-content: space-between;
    align-items: center;
    padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
    border-top: 1px solid ${cssTheme.colors.border.neutral.secondary};
  }

  &.--vertical {
    gap: ${cssTheme.layout.spacing.s300};
    padding-bottom: ${cssTheme.layout.spacing.s300};
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
  }
`

const Scale = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
`

const Swatches = styled.div`
  display: flex;
  gap: ${cssTheme.layout.spacing.s250};
`

const Swatch = styled.div`
  height: ${cssTheme.layout.spacing.s500};
  width: ${cssTheme.layout.spacing.s500};
  border-radius: ${cssTheme.layout.radius.sm};

  &.--cheaper {
    background-color: ${cssTheme.colors.background.success.secondary};
  }
  &.--neutral {
    background-color: ${cssTheme.colors.background.neutral.secondary};
  }
  &.--pricier {
    background-color: ${cssTheme.colors.warning.c200};
  }
`

interface Props {
  currency: string
  variant: PricePredictionProps['variant']
}

export const PricePredictionLegend: React.FC<Props> = ({currency, variant}) => (
  <Wrapper className={cx(`--${variant}`)}>
    <Scale>
      <Text variant="bodyXS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper id="cheaper" defaultMessage="Cheaper" />
      </Text>
      <Swatches>
        <Swatch className="--cheaper" />
        <Swatch className="--neutral" />
        <Swatch className="--pricier" />
      </Swatches>
      <Text variant="bodyXS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper id="pricier" defaultMessage="Pricier" />
      </Text>
    </Scale>
    <Box
      display="flex"
      alignItems={variant === 'horizontal' ? 'flex-end' : 'center'}
      flexDirection="column"
    >
      <Text variant="bodyXS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          id="averageNightlyPriceAcrossDealsWithTaxesAndFees"
          defaultMessage="Average nightly price across deals, with taxes & fees."
        />
      </Text>
      <Text variant="bodyXS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          id="pricesInCurrencyForGuestsUpdatedDaily"
          defaultMessage="Prices in {currency} for {guests, plural, one {# guest} other {# guests}}, updated daily."
          values={{currency, guests: 2}}
        />
      </Text>
    </Box>
  </Wrapper>
)
